import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validator, Validators } from "@angular/forms";
import {
  DialogDefinition,
  DialogField,
  PlatformDialog
} from "@shared/components/platform-tool/platform-tool-dialog-definition/models/dialog-defnition-models";
import { PlatformDialogService } from "@shared/components/platform-tool/platform-tool-dialog-definition/services/platform-dialog.service";
import {
  DetailsInstructionService,
  InstructionDetailsSourceViewType
} from '@shared/services/details-instruction-service';

import { EliteFormManagerService } from "@features/elite-form/services/elite-form-manager.service";
import { EliteFormServiceFactory } from "@features/elite-form/services/elite-form-service-factory.service";
import { EliteFormValidationService } from '@features/elite-form/services/form-validation.service';
import { BehaviorSubject, Subject } from "rxjs";
import {filter, map, take, takeUntil} from "rxjs/operators";
import { ValidatorHelper } from "@shared/components/platform-tool/platform-tool-dialog-definition/models/validators-model";
import {FormResponseViewService} from "@features/elite-form/services/form-response-view.service";
import { PlatformCommand } from '@shared/commands/command-base';
import { Api } from '@shared/constants/common-field.constants';
import { CommandService } from '@core/services/command.service';
import { EliteFormFlowChartDataService } from "@features/elite-form/services/elite-form-flow-chart-data.service";


@Component({
    standalone: false,
    selector: 'app-elite-form-response-page-single-section',
  templateUrl: './elite-form-response-page-single-section.component.html',
  styleUrls: ['./elite-form-response-page-single-section.component.scss']
})
export class EliteFormResponsePageSingleSectionComponent implements OnInit, OnDestroy {
  @Input() eliteFormId: string;
  @Input() platformDialog: PlatformDialog;
  @Input() inputForm: FormGroup;
  @Input() itemId: string;
  @Output() fieldValueChanges = new EventEmitter();
  // FM-E-55: bubbled up when a per-section Include succeeds (Reload:true) so the container
  // re-fetches dialogs through getPlatformDialogsByFlowchartId (validators restored) — never a local flip.
  @Output() sectionIncluded = new EventEmitter<void>();
  // dialogFields = [];
  dialogFields$: BehaviorSubject<any[]> = new BehaviorSubject([]);
  isReady: boolean;
  selectedDialogDefinition: DialogDefinition;
  formGridStyle = {};
  fieldVisibilities = {};
  //
  private totalRowSpan: number;
  private totalColumnSpan: any;
  private formValue = {};
  private skipValueChange: any[];
  private unSubscribe$ = new Subject<void>();
  private dialogFields: DialogField[];
  private eliteFormManagerService: EliteFormManagerService;

  groupErrors: number = 0;
  totalErrors: number = 0;
  private destroy$ = new Subject<void>();
  public fieldDefaultValueChanged: boolean;


  constructor(private platformDialogService: PlatformDialogService,
    private validationService: EliteFormValidationService,
    private formResponseViewService: FormResponseViewService,
    private detailsInstructionService: DetailsInstructionService,
    private commandService: CommandService,
    private eliteFormFlowChartDataService: EliteFormFlowChartDataService,
    private eliteFormServiceFactory: EliteFormServiceFactory) {
  }

  // FM-E-55 server-stamped section flags (PlatformDialogsByFlowchartIdQueryHandler). The Include
  // affordance renders off SectionCanInclude, which already folds in the Admin whole-form override,
  // so a locked form never shows the button.
  get sectionCanInclude(): boolean {
    return !!this.platformDialog?.fieldValues?.['SectionCanInclude'];
  }
  get isSectionIncluded(): boolean {
    return !!this.platformDialog?.fieldValues?.['SectionIncluded'];
  }

  ngOnInit(): void {
    this.validationService.registerFormGroup(this.platformDialog.id, this.inputForm, this.unSubscribe$);

    this.validationService.getValidationState()
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe(state => {
        this.groupErrors = state.groupErrors[this.platformDialog.id] || 0;
        this.totalErrors = state.totalErrors;
      });

    this.eliteFormManagerService = this.eliteFormServiceFactory.getEliteFormManagerService(this.eliteFormId);
    const valueContainingFields = [].concat(...this.platformDialog.dialogDefinitions.map(o => o.fields.filter(p => p.value).map(f => f.key)));
    this.skipValueChange = valueContainingFields.reduce((acc, value) => ({ ...acc, [value]: true }), {});
    //
    this.platformDialogService.init();
    this.selectedDialogDefinition = this.platformDialog.dialogDefinitions[0];

    this.inputForm.valueChanges.pipe(takeUntil(this.unSubscribe$)).subscribe(formValue => {
      this.onFormValueChanges(formValue);
    })
    this.formResponseViewService.fieldStateChanged$(this.itemId)
      .pipe(
        takeUntil(this.unSubscribe$)
      )
      .subscribe(formState => {
        const fieldVisibilities = formState.visibilities;
        const dialogFields = this.processFields(fieldVisibilities);
        // this.processFieldRequirements(dialogFields, formState.requirements)
        this.processForm(dialogFields, formState);
        this.dialogFields$.next(dialogFields);
      });

  }
  // filteredFields mutatedFields
  private processFields(fieldVisibilities) {
    let mutatedFields = JSON.parse(JSON.stringify(this.selectedDialogDefinition.fields));
    mutatedFields = mutatedFields.filter(o => fieldVisibilities[o.key]);
    mutatedFields.sort((a, b) => a.row - b.row);
    let dQ = mutatedFields[0].row - 1;
    const maxRow = mutatedFields[mutatedFields.length-1].row;
    for(let i=mutatedFields[0].row; i<=maxRow; i++){
      const currentRowFields = mutatedFields.filter(o => o.row == i);
      if(currentRowFields.length == 0) {
        dQ = dQ+1;
        continue;
      }
      currentRowFields.forEach(field => {
        field.row = field.row-dQ;
      });

    }

    for (const fieldKey in fieldVisibilities) {
      if (this.dialogFields) {
        if (fieldVisibilities[fieldKey] && !mutatedFields.find(o => o.key === fieldKey)) {
          this.skipValueChange[fieldKey] = true;
        }
      }
    }
    this.dialogFields = mutatedFields;
    return mutatedFields;
  }

  onFieldValueChange(event, field: DialogField) {
    field.fieldValues['DefaultValueChanged'] = event.defaultValueChanged;
    const sourceField = this.selectedDialogDefinition.fields.find(o => o.key === field.key);
    if(sourceField){
      sourceField.fieldValues['DefaultValueChanged'] = event.defaultValueChanged;
    }
    this.formValue[field.key] = event.value;
    const formInfo = {
      fieldKey: field.key,
      fieldValue: event.value,
    }
    if (!this.skipValueChange[field.key] &&
      this.inputForm.value[field.key] !== event.value &&
      this.inputForm.get(field.key) &&
      (!event.value || this.inputForm.get(field.key).valid)
    ) {
      this.fieldValueChanges.emit(formInfo)
    }
    this.skipValueChange[field.key] = false;
  }
  onFieldValidationChange(event) {
    //
  }
  onFieldDestroy(event: any) {
    //
  }
  private processForm(dialogFields: any[], formState) {
    this.isReady = false;
    const rowDict = {};
    this.totalRowSpan = 0;

    dialogFields.forEach((field: DialogField) => {
      if (!rowDict[field.row]) {
        this.totalRowSpan += field.rowSpan;
        rowDict[field.row] = true;
      }
    });

    // Move validation logic to service
    this.validationService.processFormValidation(this.inputForm, dialogFields, formState.requirements);
    // if (formState.requirements) {
    //   this.processFieldRequirements(dialogFields, formState.requirements);
    // }
    this.setGridStyle(dialogFields);
    this.isReady = true;
  }


  private setGridStyle(dialogFields: any[]) {
    dialogFields.forEach(field => {
      field.styles['grid-row'] = `${field.row}/${field.row + field.rowSpan}`;
      field.styles['grid-column'] = `${field.column}/${field.column + field.columnSpan}`;
    });
    //
    const isRowVisitedDict = {};
    const rowSpans: { size: string, count: number }[] = [];
    dialogFields.forEach(field => {
      if (!isRowVisitedDict[field.row]) {
        const size = field.height ? field.height : 'minmax(30px, auto)';
        if (!rowSpans.length || rowSpans[rowSpans.length - 1].size !== size) {
          rowSpans.push({ size: size, count: 1 });
        } else {
          if (rowSpans[rowSpans.length - 1].size === size) {
            rowSpans[rowSpans.length - 1].count++;
          }
        }
        isRowVisitedDict[field.row] = true;
      }
    });
    let rowStyle = '';
    let closeFakeDiv = false;
    rowSpans.forEach(rowSpan => {
      if (rowSpan.size.includes('fr')) {
        closeFakeDiv = true;
      }
      if (rowSpan.count === 1) {
        rowStyle = `${rowStyle}${rowSpan.size} `;
      } else {
        rowStyle = `${rowStyle}repeat(${rowSpan.count}, ${rowSpan.size}) `;
      }
    });
    rowStyle = closeFakeDiv ? `${rowStyle}auto` : `${rowStyle}1fr`;
    this.formGridStyle = {
      'grid-template-rows': `${rowStyle}`,
      'grid-template-columns': `repeat(12, minmax(auto, 1fr))`
    };
    if (this.selectedDialogDefinition?.gridGap) {
      this.formGridStyle['grid-gap'] = `${this.selectedDialogDefinition?.gridGap}px`;
    }
  }
  getFakeDivStyle() {
    return {
      'grid-row': `${this.totalRowSpan + 1}/${this.totalRowSpan + 2}`,
      'grid-column': `1/${this.totalColumnSpan}`
    };
  }

  private onFormValueChanges(formValue: any) {
  }

  openDetailsAndInstructions(field: any) {
    this.detailsInstructionService.setViewDetail(field.fieldValues['Details'],true);
  }

  onLockField(field: DialogField, lockStatus: boolean): void {
    // TODO: implement lock field logic
    const lockFieldCommand = new PlatformCommand('LockFormFieldCommand', Api.FilingManagerApi);
    lockFieldCommand.fieldValues['FieldKey'] = field.key;
    lockFieldCommand.fieldValues['IsLocked'] = lockStatus;
    lockFieldCommand.fieldValues['ItemId'] = this.itemId;
    this.commandService.executePlatformCommand(lockFieldCommand).pipe(take(1)).subscribe(res => {
      this.platformDialogService.changeLockStatus(field.key, lockStatus);
      field.fieldValues['IsLocked'] = lockStatus;
      field.isDisabled = lockStatus;
      const dialogField = this.selectedDialogDefinition.fields.find(o => o.key === field.key);
      dialogField.fieldValues['IsLocked'] = lockStatus;
      dialogField.isDisabled = lockStatus;
      /*if(lockStatus){
        this.inputForm.controls[dialogField.key].disable();
      } else{
        this.inputForm.controls[dialogField.key].enable();
      }*/
    });
  }

  // FM-E-55: scope this one read-only section back into edit. Dispatch the Edit-gated
  // IncludeEliteFormSectionCommand; on Reload:true bubble up so the container re-fetches the whole
  // page (fields arrive with validators restored). Deliberately NOT a local isDisabled/flag flip
  // like onLockField — that never round-trips and would leave the section editable-but-unvalidated.
  onIncludeSection(): void {
    const flowchartSectionId = this.platformDialog?.fieldValues?.['FlowchartSectionId'];
    const responseId = this.eliteFormFlowChartDataService.responseId;
    if (!flowchartSectionId || !responseId) {
      return;
    }
    const includeCommand = new PlatformCommand('IncludeEliteFormSectionCommand', Api.FilingManagerApi);
    includeCommand.fieldValues['ResponseId'] = responseId;
    includeCommand.fieldValues['FlowchartSectionId'] = flowchartSectionId;
    this.commandService.executePlatformCommand(includeCommand).pipe(take(1)).subscribe(res => {
      if (res?.fieldValues?.['Reload']) {
        this.sectionIncluded.emit();
      }
    });
  }

  onRestoreField(field: DialogField): void {
    const defaultValue = field.fieldValues['DefaultValue'];
    this.inputForm.get(field.key).setValue(defaultValue);
  }
  trackById(index: number, field: DialogField): string {
    return field.key;
  }
  ngOnDestroy() {
    // this.dialogFields$.complete();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
    this.validationService.unregisterFormGroup(this.platformDialog.id);
  }

  private processFieldRequirements(dialogFields: any, requirements: any) {
    dialogFields.forEach((dialogField: DialogField) => {
      const fieldFormControl = this.inputForm.get(dialogField.key);
      const requiredValidator = ValidatorHelper.getValidatorFn(this.inputForm, 'Required');
      if(this.hasValidator(fieldFormControl, requiredValidator) && !requirements[dialogField.key]){
        dialogField.validator = dialogField.validator.filter(o => o !== 'Required');
        fieldFormControl.removeValidators([requiredValidator])
        this.inputForm.get(dialogField.key).updateValueAndValidity();
      }
      if(!this.hasValidator(fieldFormControl, requiredValidator) && requirements[dialogField.key]){
        if(!dialogField.validator.includes('Required')){
          dialogField.validator.push('Required');
        }
        fieldFormControl.addValidators([requiredValidator]);
        this.inputForm.get(dialogField.key).updateValueAndValidity();
      }
    });
  }
  private hasValidator(control: AbstractControl, validator: any): boolean {
    if (!control.validator) return false; // No validators present
    const validatorFn = control.validator({} as AbstractControl);
    return validatorFn && validatorFn[validator.name];
  }
}
